import React from 'react';

// Alert, Badge, Spinner — drop-in replacements for the react-bootstrap equivalents.

type AlertVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

const ALERT: Record<AlertVariant, string> = {
  primary: 'tw-bg-[#cfe2ff] tw-text-[#084298] tw-border-[#b6d4fe]',
  secondary: 'tw-bg-[#e2e3e5] tw-text-[#41464b] tw-border-[#d3d6d8]',
  success: 'tw-bg-[#d1e7dd] tw-text-[#0f5132] tw-border-[#badbcc]',
  danger: 'tw-bg-[#f8d7da] tw-text-[#842029] tw-border-[#f5c2c7]',
  warning: 'tw-bg-warning tw-text-warning-text tw-border-[#ffecb5]',
  info: 'tw-bg-[#cff4fc] tw-text-[#055160] tw-border-[#b6effb]',
  light: 'tw-bg-surface-alt tw-text-content tw-border-line',
  dark: 'tw-bg-[#d3d3d4] tw-text-[#141619] tw-border-[#bcbebf]',
};

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  dismissible?: boolean;
  onClose?: () => void;
}
export const Alert: React.FC<AlertProps> & { Heading: React.FC<React.HTMLAttributes<HTMLHeadingElement>> } = ({
  variant = 'primary',
  dismissible,
  onClose,
  className = '',
  children,
  ...rest
}) => (
  <div role="alert" className={`tw-relative tw-rounded tw-border tw-px-4 tw-py-3 ${ALERT[variant]} ${className}`} {...rest}>
    {children}
    {dismissible && (
      <button type="button" aria-label="Close" onClick={onClose} className="tw-absolute tw-right-3 tw-top-2.5 tw-text-lg tw-leading-none tw-opacity-60 hover:tw-opacity-100">
        <i className="bi bi-x-lg" />
      </button>
    )}
  </div>
);
Alert.Heading = ({ className = '', ...rest }) => <h4 className={`tw-mb-2 tw-text-lg tw-font-semibold ${className}`} {...rest} />;

type BadgeBg = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
const BADGE: Record<BadgeBg, string> = {
  primary: 'tw-bg-primary tw-text-white',
  secondary: 'tw-bg-content-muted tw-text-white',
  success: 'tw-bg-success tw-text-white',
  danger: 'tw-bg-danger tw-text-white',
  warning: 'tw-bg-[#ffc107] tw-text-black',
  info: 'tw-bg-[#0dcaf0] tw-text-black',
  light: 'tw-bg-surface-alt tw-text-content',
  dark: 'tw-bg-content tw-text-white',
};
export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  bg?: BadgeBg | (string & {});
  pill?: boolean;
  text?: 'dark' | 'white' | 'muted' | (string & {});
}
export const Badge: React.FC<BadgeProps> = ({ bg = 'primary', pill, text, className = '', children, ...rest }) => {
  const bgCls = BADGE[bg as BadgeBg] || BADGE.secondary; // fallback for unknown/legacy bg values
  const textCls = text === 'dark' ? 'tw-text-black' : text === 'white' ? 'tw-text-white' : text === 'muted' ? 'tw-text-content-muted' : '';
  return (
    <span
      data-slot="badge"
      className={`tw-inline-block tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none ${pill ? 'tw-rounded-full' : 'tw-rounded'} ${bgCls} ${textCls} ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
};

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  animation?: 'border' | 'grow';
  size?: 'sm';
  variant?: string;
  as?: React.ElementType;
}
export const Spinner: React.FC<SpinnerProps> = ({ animation = 'border', size, as, className = '', role = 'status', children, ...rest }) => {
  const Comp: React.ElementType = as || 'div';
  const dim = size === 'sm' ? 'tw-h-4 tw-w-4 tw-border-2' : 'tw-h-8 tw-w-8 tw-border-[3px]';
  const grow = size === 'sm' ? 'tw-h-4 tw-w-4' : 'tw-h-8 tw-w-8';
  const cls =
    animation === 'grow'
      ? `tw-inline-block tw-animate-ping tw-rounded-full tw-bg-current tw-opacity-75 ${grow} ${className}`
      : `tw-inline-block tw-animate-spin tw-rounded-full tw-border-current tw-border-r-transparent ${dim} ${className}`;
  // Children (e.g. a "Loading..." label) are rendered visually-hidden for screen readers.
  return (
    <Comp role={role} className={cls} {...rest}>
      {children ? <span className="tw-sr-only">{children}</span> : null}
    </Comp>
  );
};

export default Alert;
